import { VscRefresh } from '@react-icons/all-files/vsc/VscRefresh'
import { QRCodeSVG } from 'qrcode.react'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '~/components/common/Button'
import { Input } from '~/components/common/CurieInput'
import { SiteLayout } from '~/components/layout/SiteLayout'
import { useUserContext } from '~/contexts/UserContext'
import { useGenerateMFASecret } from '~/hooks/mfa/useGenerateMFASecret'
import { verifyMFACode } from '~/services/mfaService'
import { queryClient, QueryKeys } from '~/services/queryClient'
import { completeOnboarding } from '~/services/userService'
import { BetterToast } from '~/utils/toast'

export const Page: FC = () => {
  const { refetch, user } = useUserContext()
  const [verificationCode, setVerificationCode] = useState<string>('')
  const {
    isSuccess: genMfaSecretSuccess,
    error: genMfaSecretError,
    data: secretData
  } = useGenerateMFASecret()
  const [isDisabled, setIsDisabled] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (!user) return
    // If user is already onboarded, redirect them away from MFA setup
    if (user.onboarded) {
      const returnUrl = searchParams.get('return_url')
      if (returnUrl) {
        const decodedReturnUrl = decodeURIComponent(returnUrl)
        navigate(decodedReturnUrl, { replace: true })
      } else {
        navigate('/', { replace: true })
      }
    }
    // If user is NOT onboarded, do nothing (show MFA setup form)
  }, [user, searchParams, navigate])

  const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 6) return
    setVerificationCode(e.target.value)
  }

  const handleSkip = async () => {
    try {
      setIsDisabled(true)
      await completeOnboarding()
      await refetch()
    } catch (error) {
      console.error('Error completing onboarding:', error)
      BetterToast.error('Failed to complete onboarding. Please try again.')
      setIsDisabled(false)
    }
  }

  const handleSubmit = async () => {
    if (!/^\d{6}$/.test(verificationCode)) {
      BetterToast.error('Invalid verification code')
      return
    }
    setIsDisabled(true)
    const { success, message } = await verifyMFACode(verificationCode)
    if (!success) {
      queryClient.invalidateQueries({ queryKey: QueryKeys.MfaCode })
      setVerificationCode('')
      BetterToast.error(message)
      setIsDisabled(false)
      return
    }

    BetterToast.success('MFA setup successful')
    handleSkip()
  }

  const handleRegenerateQRCode = () => {
    queryClient.invalidateQueries({ queryKey: QueryKeys.MfaCode })
    setVerificationCode('')
  }

  return (
    <SiteLayout disableLogoLink>
      <div className='flex flex-col  max-w-2xl mx-auto h-full w-full'>
        <div className='w-full max-w-[500px] text-center space-y-4 p-5 pt-24'>
          <h1 className='headline-lg-bold select-none'>Set up Authenticator MFA</h1>
          <h2 className='headline-sm-normal select-none'>
            Enhance account security with an authenticator app
          </h2>
          <div className='w-52 h-52 mx-auto bg-gray-200'>
            {genMfaSecretError ? (
              <p className='mx-auto body-lg-normal select-none'>Error generating QR code</p>
            ) : !genMfaSecretSuccess ? (
              <p className='mx-auto body-lg-normal select-none'>Generating QR code...</p>
            ) : (
              <QRCodeSVG value={secretData?.provisioning_uri} className='mx-auto w-full h-full' />
            )}
          </div>
          <p className='mx-auto body-lg-normal select-none'>
            Scan this QR code with your authenticator app or enter the secret manually:
          </p>
          <div className='mx-auto headline-sm-bold select-text'>{secretData?.secret}</div>
          <Input
            type='text'
            placeholder='Enter 6-digit code'
            value={verificationCode}
            onChange={handleVerificationCodeChange}
            className='label-md-normal'
          />
          <div className='flex justify-center mb-4'>
            <Button
              variant='outlined'
              onClick={handleRegenerateQRCode}
              disabled={isDisabled}
              className='body-md-normal'
            >
              <VscRefresh size={16} />
              Regenerate QR Code
            </Button>
          </div>

          <div className='flex justify-between w-full mx-auto'>
            <Button
              variant='outlined'
              onClick={handleSkip}
              disabled={isDisabled}
              className='body-lg-bold'
              data-testid='skip-mfa-button'
            >
              Skip for now
            </Button>
            <Button
              variant='primary'
              onClick={handleSubmit}
              disabled={isDisabled}
              className='body-lg-bold'
            >
              Enable 2FA
            </Button>
          </div>
        </div>
      </div>
    </SiteLayout>
  )
}

export default Page
